// Dynamic configuration utility
// Automatically detects the current host and port for flexible development

/**
 * Get the current base URL dynamically
 * @returns {string} The current base URL
 */
export function getCurrentBaseUrl() {
  if (typeof window === 'undefined') {
    // Server-side rendering or build time
    return import.meta.env.VITE_APP_BASE_URL || 'http://localhost:5173';
  }
  
  // Client-side: use current window location
  const { protocol, hostname, port } = window.location;
  
  // In development, use the current port
  if (import.meta.env.DEV) {
    return `${protocol}//${hostname}${port ? ':' + port : ''}`;
  }
  
  // In production, use the configured base URL or current origin
  return import.meta.env.VITE_APP_BASE_URL || window.location.origin;
}

/**
 * Get the OAuth redirect URI dynamically
 * @returns {string} The OAuth redirect URI
 */
export function getOAuthRedirectUri() {
  const baseUrl = getCurrentBaseUrl();
  return `${baseUrl}/auth/google/callback`;
}

/**
 * Get the webhook URL based on environment
 * @param {boolean} useTest - Whether to use test webhook URL
 * @returns {string} The webhook URL
 */
export function getWebhookUrl(useTest = false) {
  // Always use direct N8N URL (both development and production)
  if (useTest) {
    return import.meta.env.VITE_N8N_WEBHOOK_URL_TEST || 'https://prismdigitallabs.app.n8n.cloud/webhook-test/extract-camp-emails';
  }
  
  return import.meta.env.VITE_N8N_WEBHOOK_URL_PROD || 'https://prismdigitallabs.app.n8n.cloud/webhook/extract-camp-emails';
}

/**
 * Get the webhook proxy URL for development
 * @returns {string} The webhook proxy URL
 */
export function getWebhookProxyUrl() {
  if (import.meta.env.DEV) {
    const baseUrl = getCurrentBaseUrl();
    return `${baseUrl}/api/webhook/extract-camp-emails`;
  }
  
  // Production: use direct URL
  return getWebhookUrl(false);
}

/**
 * Check if we're running on a localhost port
 * @returns {boolean} True if running on localhost
 */
export function isLocalhost() {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

/**
 * Get the current port number
 * @returns {number|null} The current port number
 */
export function getCurrentPort() {
  if (typeof window === 'undefined') return null;
  return parseInt(window.location.port) || null;
}

/**
 * Log current configuration for debugging
 */
export function logCurrentConfig() {
  if (import.meta.env.DEV) {
    console.log('🔧 Dynamic Configuration:');
    console.log('  Base URL:', getCurrentBaseUrl());
    console.log('  OAuth Redirect URI:', getOAuthRedirectUri());
    console.log('  Webhook Proxy URL:', getWebhookProxyUrl());
    console.log('  Production Webhook URL:', getWebhookUrl(false));
    console.log('  Test Webhook URL:', getWebhookUrl(true));
    console.log('  Is Localhost:', isLocalhost());
    console.log('  Current Port:', getCurrentPort());
  }
}
